import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: "#about" },
    { label: "Method", href: "#method" },
    { label: "Coaching", href: "#packages" },
    { label: "My Tribe", href: "/tribe", isPage: true },
    { label: "Apply", href: "#apply" },
  ];

  const handleNavClick = (link, e) => {
    if (link.isPage) return; // Let Link handle it
    
    if (!isHomePage) {
      // If not on homepage, navigate to homepage first
      e.preventDefault();
      window.location.href = "/" + link.href;
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? "bg-bone/95 backdrop-blur-md border-b border-graphite/5" 
            : "bg-transparent"
        }`}
        data-testid="navigation"
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className={`font-heading text-xl md:text-2xl tracking-[0.2em] uppercase transition-colors ${
                scrolled ? "text-graphite-dark" : "text-graphite-dark"
              }`}
              data-testid="nav-logo"
            >
              Elise Redfield
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              {navLinks.map((link) => (
                link.isPage ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="font-body text-sm tracking-[0.15em] uppercase transition-colors duration-300 link-underline text-graphite-dark/80 hover:text-graphite-dark"
                    data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={isHomePage ? link.href : "/" + link.href}
                    onClick={(e) => handleNavClick(link, e)}
                    className="font-body text-sm tracking-[0.15em] uppercase transition-colors duration-300 link-underline text-graphite-dark/80 hover:text-graphite-dark"
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                )
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-graphite-dark p-2"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-bone pt-24 px-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link, index) => (
                link.isPage ? (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="font-heading text-3xl tracking-[0.1em] uppercase text-graphite/80 hover:text-graphite-dark transition-colors"
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={link.href}
                    href={isHomePage ? link.href : "/" + link.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={(e) => handleNavClick(link, e)}
                    className="font-heading text-3xl tracking-[0.1em] uppercase text-graphite/80 hover:text-graphite-dark transition-colors"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </motion.a>
                )
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
